import { Quaternion, Vector3 } from '@babylonjs/core/Maths';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { SolidParticle } from '@babylonjs/core/Particles/solidParticle';
import { SolidParticleSystem } from '@babylonjs/core/Particles/solidParticleSystem';
import { IKbVectorLike, KbVector } from '@models/classes';
import { PatternFeature } from '@models/classes/features/pattern-feature';
import { MeshNode } from '@models/classes/meshes/mesh-node';
import { SketchNode } from '@models/classes/meshes/sketch-node';
import { ITrackedClass } from '@models/interfaces/tracked-class';
import { SubmeshVertexData } from '@view/helpers/geometry-helper';
import { FeatureRenderer } from './feature';
import { ensureNormalsExist } from './utils';

type ParticleUpdaterFunction = (
    result: SolidParticle,
    iterator: IterableIterator<ParticleUpdaterFunction>,
    index?: number
) => void;
interface IPatternDef {
    sps: SolidParticleSystem;
    size: number;
    resultMesh?: Mesh;
    sourceNode: MeshNode;
    featureUpdaters: Map<string, ParticleUpdaterFunction>;
    needsUpdate: boolean; // Whether we need to reposition the particles on this pattern
    needsRebuild: boolean; // Whether we need to rebuild the pattern mesh to create less or more geometry polygons
    needsSubmeshRebuild: boolean; // Whether we need to rebuild the submesh data on the pattern mesh
    shape: Mesh;
}
const patternStore = new Map<string, IPatternDef>();
const identityRotation = Quaternion.Identity();

export abstract class PatternRenderer extends FeatureRenderer<PatternFeature> {
    update3dInternal() {}

    updateMeshGeometry(
        feature: PatternFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>,
        setParticlePositionFunc?: (idx: number, particle: SolidParticle) => void
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges);

        const meshNode = feature._parent!;
        const mesh = this.viewer.getRendererNode(meshNode);

        if (mesh) {
            /** See line 156 */
            // let _pattern = patternStore.get(meshNode._dynamicId);
            let _pattern = null;

            // If there are no enabled pattern features before this one, update the pattern shape as some geometry may have changed
            if (!_pattern) {
                const meshUpdated = new Mesh('patternTempGeometry');
                meshUpdated.setEnabled(false);
                const tempVertexData = new SubmeshVertexData();
                tempVertexData.copyFrom(newVertexData);
                tempVertexData.applyToMesh(meshUpdated, false);

                ensureNormalsExist(meshUpdated);

                const sps = new SolidParticleSystem(`${mesh.id}_${feature._dynamicId}`, this.scene, {
                    enableDepthSort: true,
                    enableMultiMaterial: true,
                    expandable: true,
                });

                if (feature) {
                    if (feature.meshId) {
                        const sketchNode = this.viewer.getNode(feature.meshId);
                        if (sketchNode instanceof SketchNode) {
                            let distance = feature.distance;
                            if (distance <= 0) {
                                distance = 1;
                            }
                            const positionArray = this.getPositionArray(
                                sketchNode,
                                feature.distance,
                                feature.addEndMesh
                            );
                            sps.addShape(meshUpdated, positionArray.length);
                            sps.counter = positionArray.length;
                            feature.count = positionArray.length;
                            const sketchPosition = sketchNode.position.toVec3();
                            sps.initParticles = () => {
                                for (let p = 0; p < sps.counter; p++) {
                                    const particle = sps.particles[p];
                                    particle.position = positionArray[p].add(sketchPosition);
                                }
                            };
                        }
                    } else {
                        sps.addShape(meshUpdated, feature.count + 1);
                        sps.initParticles = () => {
                            for (let p = 0; p < sps.nbParticles; p++) {
                                const particle = sps.particles[p];
                                if (particle.rotationQuaternion) {
                                    particle.rotationQuaternion!.copyFrom(identityRotation);
                                } else {
                                    particle.rotationQuaternion = Quaternion.Identity();
                                }
                                particle.position.setAll(0);
                            }
                        };
                    }
                }

                const newPattern: IPatternDef = {
                    sps,
                    size: 1,
                    featureUpdaters: new Map(),
                    sourceNode: meshNode,
                    needsUpdate: true,
                    needsRebuild: true,
                    needsSubmeshRebuild: true,
                    shape: meshUpdated,
                };
                sps.updateParticle = particle => {
                    // Process the last update function as it will call the prior ones recursively
                    const updates = newPattern.featureUpdaters.values();
                    const update: ParticleUpdaterFunction = updates.next().value;
                    update(particle, updates);
                    // Do we need this?
                    // particle.scaling.copyFrom(mesh.scaling);
                    return particle;
                };
                patternStore.set(meshNode._dynamicId, newPattern);
                _pattern = newPattern;
            }
            const pattern = _pattern;
            pattern.needsUpdate = true;

            pattern.featureUpdaters.set(feature._dynamicId, (particle, iterator, index) => {
                if (feature.enabled) {
                    const particleIndex = particle.idx;
                    if (particleIndex >= pattern.size) {
                        particle.isVisible = false;
                        return;
                    }
                    if (!particle.isVisible) {
                        particle.isVisible = true;
                    }
                    const featureItemIndex = index !== undefined ? index : particleIndex;

                    const i = featureItemIndex % feature.count;
                    if (i > 0 && setParticlePositionFunc) {
                        setParticlePositionFunc(i, particle);
                    }

                    const nextUpdateFunction: ParticleUpdaterFunction = iterator.next().value;
                    if (nextUpdateFunction) {
                        nextUpdateFunction(particle, iterator, Math.floor(featureItemIndex / feature.count));
                    }
                }
            });

            pattern.size = Array.from(pattern.featureUpdaters.keys()).reduce((result, featureId) => {
                const feature = meshNode.features.find(feature => feature._dynamicId === featureId) as PatternFeature;
                return result * feature!.count;
            }, 1);

            if (
                !pattern.needsRebuild &&
                (vertexDataChanges.indices || vertexDataChanges.positions || vertexDataChanges.uvs)
            ) {
                /**
                 * If the shape of the mesh changed, then we have to remove and re-add every particle to recompute the geometry.
                 * Potential optimization here is to only recompute vertex positions if the vertex count is the same,
                 * and only do the following expensive operation if the vertex count (indices?) changes.
                 */
                const currentSize = pattern.sps.nbParticles;
                pattern.sps.addShape(pattern.shape, Math.max(pattern.size, pattern.sps.nbParticles));
                pattern.sps.removeParticles(0, currentSize - 1);
                pattern.needsRebuild = true;
            } else if (pattern.sps.nbParticles < pattern.size) {
                // If we don't have enough particles to complete the pattern, expand the SPS as needed
                pattern.sps.addShape(pattern.shape, pattern.size - pattern.sps.nbParticles);
                pattern.needsRebuild = true;
            }

            if (vertexDataChanges.submeshData) {
                pattern.needsSubmeshRebuild = true;
            }

            /**
             * We are removing the pattern feature combination system because it was causing a lot of issues around the cache, with enabling/disabling and reordering features.
             * In the future we will just add multi-direction properties into the pattern feature instead.
             */
            // const nextFeature = meshNode.features[thisIndex + 1];
            // if (!nextFeature || !(nextFeature instanceof PatternFeature)) {
            this.processParticleSystem(pattern, newVertexData); // mutates newVertexData
            patternStore.delete(meshNode._dynamicId); // Delete the pattern since we are done processing it
            // }
        }

        return newVertexData;
    }

    processParticleSystem(pattern: IPatternDef, resultVertexData: Partial<SubmeshVertexData>) {
        if (!pattern.needsUpdate) {
            return;
        }

        // Shrink the particle system if there are way too many particles (more than 50% too many)
        // We don't want to shrink unless necessary beca use there's a performance hit to rebuilding the mesh
        if (pattern.sps.nbParticles - pattern.size > pattern.size / 2) {
            pattern.sps.removeParticles(pattern.size, pattern.sps.nbParticles - 1);
            pattern.needsRebuild = true;
        }

        pattern.sps.initParticles();

        if (!pattern.resultMesh || pattern.needsRebuild) {
            pattern.resultMesh = pattern.sps.buildMesh();
            pattern.needsRebuild = false;
            pattern.needsSubmeshRebuild = true;
            pattern.resultMesh.setEnabled(false);
        }

        pattern.sps.computeBoundingBox = true;
        pattern.sps.setParticles();

        const totalIndicesSourceCount = resultVertexData.indices!.length;
        const totalVerticesSourceCount = resultVertexData.positions!.length / 3;

        const newVertexData = VertexData.ExtractFromMesh(pattern.resultMesh);
        resultVertexData.indices = newVertexData.indices;
        resultVertexData.positions = newVertexData.positions;
        if (newVertexData.normals) {
            resultVertexData.normals = newVertexData.normals;
        }
        if (resultVertexData.uvs && newVertexData.uvs) {
            // Don't add UVs if UVs never existed
            resultVertexData.uvs = newVertexData.uvs;
        }

        // If the source mesh has submeshes, then we need to update them
        if (resultVertexData.submeshData) {
            const submeshesToClone = resultVertexData.submeshData.slice(1);

            const totalIndicesResultCount = resultVertexData.indices!.length;
            const totalVerticesResultCount = resultVertexData.positions!.length / 3;
            // resultMeshCsg.dispose();
            const baseMaterialIndex = resultVertexData.submeshData[0].materialIndex;

            resultVertexData.submeshData = [];
            resultVertexData.submeshData.push({
                materialIndex: baseMaterialIndex,
                verticesStart: 0,
                verticesCount: totalVerticesResultCount,
                indexStart: 0,
                indexCount: totalIndicesResultCount,
            });

            const spsPatternSize = totalVerticesResultCount / totalVerticesSourceCount;

            if (submeshesToClone.length > 0) {
                submeshesToClone.forEach(subMesh => {
                    for (let i = 0; i < spsPatternSize; i++) {
                        resultVertexData.submeshData!.push({
                            materialIndex: subMesh.materialIndex,
                            verticesStart: subMesh.verticesStart + i * totalVerticesSourceCount,
                            verticesCount: subMesh.verticesCount,
                            indexStart: subMesh.indexStart + i * totalIndicesSourceCount,
                            indexCount: subMesh.indexCount,
                        });
                    }
                });
            }
        }
    }

    getPositionArray(node: SketchNode, distance: number, addEndMesh: boolean) {
        const result: Vector3[] = [];
        const sketchLength = node.getSketchLength();
        const numberOfMeshes = sketchLength / distance;
        const percentagePoints: number[] = [];
        for (let i = 0; i < numberOfMeshes; i++) {
            percentagePoints.push((1 / numberOfMeshes) * i);
        }

        percentagePoints.forEach(point => {
            result.push(this.getIntervalPosition(node, point));
        });

        if (addEndMesh) {
            const lastPath = node.paths[node.paths.length - 1];
            const lastPoint = lastPath._points[lastPath._points.length - 1];
            const lastPointVec3 = new Vector3(lastPoint.x, lastPoint.y, lastPoint.z);
            result.push(lastPointVec3);
        }

        return result;
    }

    protected getIntervalPosition(node: SketchNode, posZeroToOne: number) {
        const result = new Vector3(0, 0, 0);
        const sketchLength = node.getSketchLength();
        let trackedLength = 0;
        let targetNotFound = true;
        node.paths.forEach(path => {
            let lastPosition: IKbVectorLike | null = null;
            let lastLength = 0;
            path._points.forEach(point => {
                if (!lastPosition) {
                    lastPosition = point;
                } else {
                    const currentPos = point;
                    const distance = Math.sqrt(
                        Math.pow(lastPosition.x - currentPos.x, 2) +
                            Math.pow(lastPosition.y - currentPos.y, 2) +
                            Math.pow(lastPosition.z - currentPos.z, 2)
                    );
                    lastLength = trackedLength;
                    trackedLength += distance;
                    const distanceBetweenPoints = trackedLength - lastLength;
                    const distanceFromP1toTarget = posZeroToOne * sketchLength - lastLength;
                    const percentageBetween2Points = distanceFromP1toTarget / distanceBetweenPoints;
                    const vectorBetween2Points = new KbVector(
                        currentPos.x - lastPosition.x,
                        currentPos.y - lastPosition.y,
                        currentPos.z - lastPosition.z
                    );
                    if (trackedLength > posZeroToOne * sketchLength && targetNotFound) {
                        const calculatedPosition = vectorBetween2Points
                            .scale(percentageBetween2Points)
                            .add(lastPosition);
                        result.set(calculatedPosition.x, calculatedPosition.y, calculatedPosition.z);
                        targetNotFound = false;
                    }
                    lastPosition = currentPos;
                }
            });
            if (posZeroToOne === 1) {
                const path = node.paths[node.paths.length - 1];
                const controlPoint = path._points[path._points.length - 1];
                result.set(controlPoint.x, controlPoint.y, controlPoint.z);
            }
        });

        return result;
    }

    delete3d(feature: ITrackedClass<PatternFeature>, preserveCache?: boolean) {
        patternStore.forEach((pattern, meshNode) => {
            if (pattern.featureUpdaters.has(feature._dynamicId)) {
                pattern.featureUpdaters.delete(feature._dynamicId);
            }
            if (pattern.featureUpdaters.size === 0) {
                patternStore.delete(meshNode);
            }
        });

        super.delete3d(feature, preserveCache);
    }

    dispose() {
        super.dispose();
        patternStore.clear();
    }
}
